import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { useElevation, estElevationAnnulee } from '../../components/useElevation.js';
import { ApiError, cloturerMois, rouvrirMois } from '../../lib/api.js';
import type { MoisComptable } from '../../types.js';
import { useMessageErreur } from '../Finances/commun.js';
import commun from '../VueDAigle/commun.module.css';
import { LienConsole, Section } from '../VueDAigle/composants.js';
import { useFormats } from '../VueDAigle/formats.js';
import { moisPlus } from './donnees.js';
import styles from './Comptabilite.module.css';
import { PastilleStatut } from './MoisEnChiffres.js';
import { useNomType } from './nomsTypes.js';
import { useRelireComptabilite } from './useComptabilite.js';

/** Ce qu'une clôture refusée dit manquer (`409 cloture_impossible`, `details`). */
interface Refus {
  aSaisir: number;
  aClasser: number;
  nonReparti: string[];
}

function lireRefus(erreur: unknown): Refus | null {
  if (!(erreur instanceof ApiError) || erreur.status !== 409) return null;
  const details = erreur.details as (Partial<Refus> & { raison?: string }) | null;
  if (details?.raison !== 'cloture_impossible') return null;
  return {
    aSaisir: details.aSaisir ?? 0,
    aClasser: details.aClasser ?? 0,
    nonReparti: details.nonReparti ?? [],
  };
}

function Controle({
  ok,
  titre,
  detail,
  children,
}: {
  ok: boolean;
  titre: string;
  detail: string;
  children?: JSX.Element | null;
}): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <li className={styles.controle}>
      <div className={styles.controleTete}>
        <span className={styles.controleEtat}>
          <span
            className={`${styles.coche} ${ok ? styles.cocheOk : styles.cocheKo}`}
            aria-hidden="true"
          >
            {ok ? '✓' : '!'}
          </span>
          {titre}
        </span>
        <span className={commun.note}>
          <span className={commun.srOnly}>
            {ok ? t('comptabilite.cloture.enOrdre') : t('comptabilite.cloture.aRegler')}
            {' : '}
          </span>
          {detail}
        </span>
      </div>
      {children}
    </li>
  );
}

/**
 * La clôture du mois (direction). Elle fige les chiffres et inscrit le dû de
 * chaque partie dans ses versements. Ce qui l'empêche est dit ici, d'après les
 * compteurs du mois, avant même de demander ; si l'API la refuse malgré tout
 * (un montant saisi entre-temps), le motif revient dans cette liste. La
 * confirmation est intégrée au bloc, jamais une boîte du navigateur ; clôturer
 * et rouvrir demandent une vérification récente du second facteur.
 */
export function Cloture({
  mois,
  moisCourant,
  brouillonEnCours,
  numero,
}: {
  mois: MoisComptable;
  moisCourant: string;
  brouillonEnCours: boolean;
  numero: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const nomType = useNomType();
  const message = useMessageErreur('comptabilite');
  const relire = useRelireComptabilite();
  const { executer, dialogue } = useElevation();
  const [confirmation, setConfirmation] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [refus, setRefus] = useState<Refus | null>(null);
  const [fait, setFait] = useState<'cloture' | 'reouverture' | null>(null);

  const cloture = mois.statut === 'cloture';
  const termine = mois.mois < moisCourant;
  const aSaisir = mois.totaux.aSaisir + mois.aClasser.aSaisir;
  const aClasser = mois.aClasser.lignes;
  const sansGrille = mois.nonReparti;
  const blocages =
    (termine ? 0 : 1) +
    (aSaisir > 0 ? 1 : 0) +
    (aClasser > 0 ? 1 : 0) +
    (sansGrille.length > 0 ? 1 : 0) +
    (brouillonEnCours ? 1 : 0);
  const nomMois = f.moisLong(mois.mois);

  const lancer = async (geste: 'cloture' | 'reouverture'): Promise<void> => {
    setEnCours(true);
    setErreur(null);
    setRefus(null);
    setFait(null);
    try {
      await executer(async () => {
        if (geste === 'cloture') await cloturerMois(mois.mois);
        else await rouvrirMois(mois.mois);
      });
      setConfirmation(false);
      setFait(geste);
    } catch (echec) {
      if (estElevationAnnulee(echec)) return;
      setRefus(lireRefus(echec));
      setErreur(message(echec));
      setConfirmation(false);
    } finally {
      setEnCours(false);
      await relire();
    }
  };

  return (
    <Section
      id="cloture"
      numero={numero}
      titre={t('comptabilite.cloture.titre')}
      pastille={<PastilleStatut cloture={cloture} />}
    >
      <p className={styles.clotureTexte}>
        {cloture
          ? t('comptabilite.cloture.texteCloture', {
              date: mois.clotureLe ? f.jourMoisAnnee(mois.clotureLe) : '',
            })
          : t('comptabilite.cloture.texte')}
      </p>

      {cloture ? null : (
        <ul
          className={styles.controles}
          aria-label={t('comptabilite.cloture.aria', { mois: nomMois })}
        >
          <Controle
            ok={termine}
            titre={t('comptabilite.cloture.termine')}
            detail={
              termine
                ? t('comptabilite.cloture.termineOui')
                : t('comptabilite.cloture.termineNon', { mois: f.moisLong(moisPlus(mois.mois, 1)) })
            }
          />
          <Controle
            ok={aSaisir === 0}
            titre={t('comptabilite.cloture.montants')}
            detail={
              aSaisir === 0
                ? t('comptabilite.cloture.aucun')
                : t('comptabilite.cloture.aSaisir', { count: aSaisir })
            }
          >
            {aSaisir > 0 ? (
              <p className={styles.controleAction}>
                <LienConsole vers="/finances?onglet=abonnements">
                  {t('comptabilite.cloture.ouvrirAbonnements')}
                </LienConsole>
                <LienConsole vers="/finances?onglet=revenus">
                  {t('comptabilite.cloture.ouvrirRevenus')}
                </LienConsole>
              </p>
            ) : null}
          </Controle>
          <Controle
            ok={aClasser === 0}
            titre={t('comptabilite.cloture.recettes')}
            detail={
              aClasser === 0
                ? t('comptabilite.cloture.aucune')
                : t('comptabilite.cloture.aClasser', { count: aClasser })
            }
          >
            {aClasser > 0 ? (
              <p className={styles.controleAction}>
                <LienConsole vers="/finances?onglet=revenus">
                  {t('comptabilite.cloture.classer')}
                </LienConsole>
              </p>
            ) : null}
          </Controle>
          <Controle
            ok={sansGrille.length === 0}
            titre={t('comptabilite.cloture.grilles')}
            detail={
              sansGrille.length === 0
                ? t('comptabilite.cloture.aucun')
                : t('comptabilite.cloture.sansGrille', { count: sansGrille.length })
            }
          >
            {sansGrille.length > 0 ? (
              <p className={styles.controleAction}>
                <span>{sansGrille.map((service) => nomType(service)).join(', ')}</span>
                <a className={commun.lien} href="#pourcentages">
                  {t('comptabilite.cloture.regler')}
                </a>
              </p>
            ) : null}
          </Controle>
          {brouillonEnCours ? (
            <Controle
              ok={false}
              titre={t('comptabilite.cloture.brouillon')}
              detail={t('comptabilite.cloture.brouillonDetail')}
            />
          ) : null}
        </ul>
      )}

      {erreur ? (
        <div className={styles.erreur} role="alert">
          <p>{erreur}</p>
          {refus ? (
            <ul className={styles.refus}>
              {refus.aSaisir > 0 ? (
                <li>{t('comptabilite.cloture.aSaisir', { count: refus.aSaisir })}</li>
              ) : null}
              {refus.aClasser > 0 ? (
                <li>{t('comptabilite.cloture.aClasser', { count: refus.aClasser })}</li>
              ) : null}
              {refus.nonReparti.length > 0 ? (
                <li>
                  {t('comptabilite.cloture.sansGrilleListe', {
                    services: refus.nonReparti.map((service) => nomType(service)).join(', '),
                  })}
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      ) : null}
      {fait ? (
        <p className={styles.succes} role="status">
          {t(`comptabilite.cloture.fait.${fait}`, { mois: nomMois })}
        </p>
      ) : null}

      {confirmation ? (
        <div
          className={styles.confirmation}
          role="group"
          aria-label={t('comptabilite.cloture.confirmation')}
        >
          {cloture ? (
            <>
              <p>
                <strong>{t('comptabilite.cloture.rouvrirTitre', { mois: nomMois })}</strong>
              </p>
              <p>{t('comptabilite.cloture.rouvrirTexte')}</p>
            </>
          ) : (
            <>
              <p>
                <strong>{t('comptabilite.cloture.clotureTitre', { mois: nomMois })}</strong>
              </p>
              <p>{t('comptabilite.cloture.clotureTexte')}</p>
              {mois.parBeneficiaire.length > 0 ? (
                <ul className={styles.dus}>
                  {mois.parBeneficiaire.map((ligne) => (
                    <li key={ligne.beneficiaireId}>
                      <span>{ligne.libelle}</span>
                      <span className={commun.num}>{f.euros(ligne.montantCentimes)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p>{t('comptabilite.cloture.clotureRouvrir')}</p>
            </>
          )}
          <div className={styles.confirmationActions}>
            <Button
              loading={enCours}
              onClick={() => void lancer(cloture ? 'reouverture' : 'cloture')}
            >
              {cloture
                ? t('comptabilite.cloture.rouvrirConfirmer')
                : t('comptabilite.cloture.confirmer')}
            </Button>
            <Button variant="secondary" disabled={enCours} onClick={() => setConfirmation(false)}>
              {t('comptabilite.cloture.annuler')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.clotureActions}>
          {cloture ? (
            <Button
              variant="secondary"
              onClick={() => {
                setFait(null);
                setErreur(null);
                setConfirmation(true);
              }}
            >
              {t('comptabilite.cloture.rouvrir')}
            </Button>
          ) : (
            <>
              <Button
                disabled={blocages > 0}
                aria-describedby="comptabilite-cloture-aide"
                onClick={() => {
                  setFait(null);
                  setErreur(null);
                  setRefus(null);
                  setConfirmation(true);
                }}
              >
                {t('comptabilite.cloture.cloturer', { mois: nomMois })}
              </Button>
              <p className={commun.note} id="comptabilite-cloture-aide">
                {blocages > 0
                  ? t('comptabilite.cloture.reste', { count: blocages })
                  : t('comptabilite.cloture.pret')}
              </p>
            </>
          )}
        </div>
      )}
      {dialogue}
    </Section>
  );
}
